import { z } from 'zod';

/**
 * 施術管理者 作成・編集フォームのみ（CreateClinicLocationManagerPage / ServiceBlockFields）。
 * 変更時は BE の ClinicLocationConstants::MANAGER_WIDE_AREA_INSURER_CODE_MAX_LENGTH と揃えること。
 */
export const MANAGER_FORM_INSURER_CODE_MAX_LENGTH = 8;

const MANAGER_FORM_INSURER_CODE_MAX_MESSAGE = `保険者番号は${MANAGER_FORM_INSURER_CODE_MAX_LENGTH}文字以下にしてください。`;

/** Spec: 保険者番号・施術機関コード・県番号（長さは上記定数参照） */
const wideAreaRowSchema = z.object({
  wide_area_union_code: z
    .string()
    .nullable()
    .optional()
    .refine(
      v => !v || v.length <= MANAGER_FORM_INSURER_CODE_MAX_LENGTH,
      MANAGER_FORM_INSURER_CODE_MAX_MESSAGE
    ),
  wide_area_union_number: z
    .string()
    .nullable()
    .optional()
    .refine((v) => !v || v.length <= 100, '施術機関コードは100文字以下にしてください。'),
  prefecture_number: z
    .string()
    .nullable()
    .optional()
    .refine((v) => !v || v.length <= 2, '県番号は2文字以下にしてください。'),
});

/** Schema for a single service box (A2 or R2). Spec: validate only when click 更新/登録. */
export const serviceBlockSchema = z
  .object({
    is_none_disp: z.boolean().optional(),
    available_from: z.string().optional(),
    available_to: z.string().nullable().optional(),
    location_manager_number: z
      .string()
      .nullable()
      .optional()
      .refine((v) => !v || v.length <= 40, '登録記号番号は40文字以下にしてください。'),
    wide_area_union_rows: z
      .array(wideAreaRowSchema)
      .max(30, '保険者別番号は30件まで登録できます。')
      .optional(),
    am_time_start: z.string().nullable().optional(),
    am_time_end: z.string().nullable().optional(),
    pm_time_start: z.string().nullable().optional(),
    pm_time_end: z.string().nullable().optional(),
    is_am_monday: z.boolean().optional(),
    is_am_tuesday: z.boolean().optional(),
    is_am_wednesday: z.boolean().optional(),
    is_am_thursday: z.boolean().optional(),
    is_am_friday: z.boolean().optional(),
    is_am_saturday: z.boolean().optional(),
    is_am_sunday: z.boolean().optional(),
    is_pm_monday: z.boolean().optional(),
    is_pm_tuesday: z.boolean().optional(),
    is_pm_wednesday: z.boolean().optional(),
    is_pm_thursday: z.boolean().optional(),
    is_pm_friday: z.boolean().optional(),
    is_pm_saturday: z.boolean().optional(),
    is_pm_sunday: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (!data.available_from?.trim()) return true;
      const d = /^\d{4}-\d{2}-\d{2}$/.test(data.available_from)
        ? new Date(data.available_from)
        : null;
      return d !== null && !Number.isNaN(d.getTime());
    },
    { message: '適用開始日は正しい日付ではありません。', path: ['available_from'] }
  )
  .refine(
    (data) => {
      if (!data.available_to?.trim()) return true;
      const d = /^\d{4}-\d{2}-\d{2}$/.test(data.available_to) ? new Date(data.available_to) : null;
      if (!d || Number.isNaN(d.getTime())) return false;
      if (!data.available_from?.trim()) return true;
      const from = new Date(data.available_from);
      return from.getTime() <= d.getTime();
    },
    { message: '有効期限TOは有効期限FROM以降の日付にしてください。', path: ['available_to'] }
  );

export type ServiceBlockFormData = z.infer<typeof serviceBlockSchema>;

/** 拠点に紐づく施術者の適用期間（A2 行と R2 行を別集約） */
export type MassagerPeriodSlice = {
  available_from?: string | null;
  available_to?: string | null;
};

export type MassagerApplicationPeriod = {
  a2: MassagerPeriodSlice | null;
  r2: MassagerPeriodSlice | null;
} | null;

/** block の有効期限FROM が施術者の適用開始より前のとき */
export const MASSAGER_PERIOD_START_MSG =
  '適用開始日は、施術者の適用期間内の日付を入力してください。';

/** block の有効期限TO が施術者の適用終了より後、または施術者に終了日があるのに TO 未入力のとき */
export const MASSAGER_PERIOD_END_MSG =
  '適用終了日は、施術者の適用期間内の日付を入力してください。';

function trimYmd(s: string | null | undefined): string | null {
  const t = s?.trim();
  if (!t) return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : null;
}

/** API / UI で終了日なし相当（null または 2100-12-31） */
function effectiveMassagerEndYmd(available_to: string | null | undefined): string | null {
  const t = trimYmd(available_to ?? null);
  if (t == null || t === '2100-12-31') return null;
  return t;
}

/** 施術者の適用開始（未設定なら FROM 制約なし） */
function effectiveMassagerStartYmd(available_from: string | null | undefined): string | null {
  return trimYmd(available_from ?? null);
}

/** Schema factory: only validate enabled blocks; disabled blocks are skipped. */
export function createClinicLocationManagerSchema(
  enableA2: boolean,
  enableR2: boolean,
  massagerPeriod: MassagerApplicationPeriod = null
) {
  return z
    .object({
      massager_id: z
        .number({ required_error: '施術者は必須です。' })
        .min(1, '施術者は必須です。'),
      a2: enableA2 ? serviceBlockSchema.optional() : z.any().optional(),
      r2: enableR2 ? serviceBlockSchema.optional() : z.any().optional(),
    })
    .superRefine((data, ctx) => {
      const a2From = enableA2 ? (data.a2?.available_from?.trim() ?? '') : '';
      const r2From = enableR2 ? (data.r2?.available_from?.trim() ?? '') : '';
      if (!a2From && !r2From) {
        const msg = '適用開始日は正しい日付ではありません。';
        if (enableA2) ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg, path: ['a2', 'available_from'] });
        if (enableR2) ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg, path: ['r2', 'available_from'] });
      }

      if (!massagerPeriod) return;

      const checkBlockWithinMassagerPeriod = (
        prefix: 'a2' | 'r2',
        enabled: boolean,
        slice: MassagerPeriodSlice | null | undefined
      ) => {
        if (!enabled) return;
        const mFromStr = effectiveMassagerStartYmd(slice?.available_from ?? null);
        const mToStr = effectiveMassagerEndYmd(slice?.available_to ?? null);
        if (mFromStr == null && mToStr == null) return;

        const blockFromTrim = data[prefix]?.available_from?.trim() ?? '';
        if (!blockFromTrim) return;

        const bFromStr = trimYmd(blockFromTrim);
        if (mFromStr && bFromStr && bFromStr < mFromStr) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: MASSAGER_PERIOD_START_MSG,
            path: [prefix, 'available_from'],
          });
        }

        const bToStr = trimYmd(data[prefix]?.available_to ?? null);
        if (mToStr) {
          if (!bToStr) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: MASSAGER_PERIOD_END_MSG,
              path: [prefix, 'available_to'],
            });
          } else if (bToStr > mToStr) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: MASSAGER_PERIOD_END_MSG,
              path: [prefix, 'available_to'],
            });
          }
        }
      };

      // はり・きゅう (A2) と マッサージ (R2) それぞれ、対応する拠点施術者の適用期間内に収める
      checkBlockWithinMassagerPeriod('a2', enableA2, massagerPeriod.a2);
      checkBlockWithinMassagerPeriod('r2', enableR2, massagerPeriod.r2);
    });
}

/** Base schema for type inference (assumes both enabled). */
export const createClinicLocationManagerSchemaBase = createClinicLocationManagerSchema(true, true);

export type CreateClinicLocationManagerFormData = z.infer<typeof createClinicLocationManagerSchemaBase>;
